import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MonitorIcon from '@mui/icons-material/Monitor'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import FavoriteIcon from '@mui/icons-material/Favorite'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import { allAvailableEventTypes, BOOKED_EVENT_STEP_STATUSES } from 'src/consumer/constants'
import { useScreenSize } from 'src/customHooks/customHooks'
import { Chip, Button } from '@mui/material'

export default function StepsCheckUI({ bookedSlot, onOpenVitalsDialog }) {
  const { width } = useScreenSize()
  const isMobile = width < 768

  // Check if we're in clinic assistant portal
  const isClinicAssistantPortal = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/ca-home/') || 
    window.location.pathname.startsWith('/kiosk/') || 
    window.location.pathname.startsWith('/ca/')
  )

  const isScreeningOrTherapyStep = (step) =>
    step?.refType === allAvailableEventTypes.screening ||
    step?.refType === allAvailableEventTypes.therapySession

  const allPrevSteps = bookedSlot?.previousSteps?.filter(
    (step) =>
      isScreeningOrTherapyStep(step),
  )
  const allNextSteps = bookedSlot?.nextSteps?.filter(
    (step) =>
      isScreeningOrTherapyStep(step),
  )

  const isScreened = allPrevSteps?.some(
    (step) =>
      step?.refType === allAvailableEventTypes.screening &&
      step?.status === BOOKED_EVENT_STEP_STATUSES.COMPLETED,
  )

  // Check if vitals are recorded for this booked slot
  const hasVitals = Boolean(
    bookedSlot?.vital ||
    bookedSlot?.vitals ||
    bookedSlot?.vitalData ||
    (Array.isArray(bookedSlot?.vitals) && bookedSlot?.vitals.length > 0) ||
    (bookedSlot?.vital && Object.keys(bookedSlot.vital).length > 0)
  )

  function getStatusMessage(step) {
    const handlerName = step?.stepHandlerName
    const hasHandler = handlerName && step?.status !== BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED
    const stepType = step?.refType === allAvailableEventTypes.screening ? 'Screening' : 'Therapy'
    
    switch (step?.status) {
      case BOOKED_EVENT_STEP_STATUSES.COMPLETED:
        return hasHandler 
          ? `${stepType} completed by ${handlerName}`
          : `${stepType} completed`
      case BOOKED_EVENT_STEP_STATUSES.NOT_STARTED:
        return hasHandler
          ? `${stepType} pending (${handlerName})`
          : `${stepType} not started`
      case BOOKED_EVENT_STEP_STATUSES.IN_PROGRESS:
        return hasHandler
          ? `${stepType} in progress by ${handlerName}`
          : `${stepType} in progress`
      case BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED:
        return `${stepType} requested`
      case BOOKED_EVENT_STEP_STATUSES.STARTED:
      default:
        return hasHandler
          ? `${stepType} active (${handlerName})`
          : `${stepType} started`
    }
  }

  function handleScreeningStatus(screeningStep, statusMessage) {
    switch (screeningStep?.status) {
      case BOOKED_EVENT_STEP_STATUSES.COMPLETED:
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircleIcon style={{ color: '#16a34a', fontSize: '16px', flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </span>
        )
      case BOOKED_EVENT_STEP_STATUSES.NOT_STARTED:
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CancelIcon style={{ color: '#dc2626', fontSize: '16px', flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </span>
        )
      case BOOKED_EVENT_STEP_STATUSES.IN_PROGRESS:
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <PlayCircleIcon style={{ color: '#3b82f6', fontSize: '16px', flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </span>
        )
      case BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED:
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <HourglassBottomIcon style={{ color: '#f59e0b', fontSize: '16px', flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </span>
        )
      case BOOKED_EVENT_STEP_STATUSES.STARTED:
      default:
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MonitorIcon style={{ color: '#eab308', fontSize: '16px', flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </span>
        )
    }
  }

  if (isMobile) {
    return isScreened ? (
      <div>
        <Chip size="small" color="success" label="Screened" />
      </div>
    ) : (
      <></>
    )
  }

  const hasSteps = allPrevSteps?.length > 0 || allNextSteps?.length > 0
  const showVitalsSection = hasVitals || (isClinicAssistantPortal && onOpenVitalsDialog)

  return hasSteps || showVitalsSection ? (
    <div style={{ padding: '4px 0' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '6px',
        fontSize: '12px',
        lineHeight: '1.4'
      }}>
        {allPrevSteps?.map((step, index) => {
          if (isScreeningOrTherapyStep(step)) {
            const statusMessage = getStatusMessage(step)
            return (
              <div key={step?.id || step?.uid || `prev-step-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                {handleScreeningStatus(step, statusMessage)}
              </div>
            )
          }
          return null
        })}
        {allNextSteps?.map((step, index) => {
          if (isScreeningOrTherapyStep(step)) {
            const statusMessage = getStatusMessage(step)
            return (
              <div key={step?.id || step?.uid || `next-step-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                {handleScreeningStatus(step, statusMessage)}
              </div>
            )
          }
          return null
        })}
        {/* Vitals section - show button if no vitals, or clickable strip if vitals exist */}
        {showVitalsSection && (
          <div style={{ 
            marginTop: hasSteps ? '2px' : '0',
            paddingTop: hasSteps ? '4px' : '0',
            borderTop: hasSteps ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'
          }}>
            {hasVitals ? (
              // Clickable vitals strip with edit icon
              <div 
                onClick={onOpenVitalsDialog}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <FavoriteIcon style={{ color: '#ef4444', fontSize: '16px', flexShrink: 0 }} />
                <span>Vitals recorded</span>
                <EditIcon style={{ color: '#666', fontSize: '14px', flexShrink: 0, marginLeft: '2px' }} />
              </div>
            ) : (
              // Record Vitals button
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onOpenVitalsDialog}
                sx={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  minWidth: 'auto',
                  height: '24px',
                  textTransform: 'none',
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    borderColor: '#dc2626',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  },
                }}
              >
                Record Vitals
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  ) : (
    <></>
  )
}
